import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { Providers, PusherEventPayloadMap } from '@timelyapp/integration-cloud-api-spec';
import type {
  GetSubscribableResourcesApiArg,
  UpdateBatchOfSubscriptionsApiArg,
} from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import type { IState } from '~redux/state';

export type IntegrationConfiguration = {
  toolId: Providers | null;
  scope: 'personal' | 'account';
  modal: 'setup_wizard' | 'subscription_wizard' | 'manage' | null;
  defaultStep?: string;
  importProfileKey?: string | null;
  reAuthConnectionId?: string;
};

// Type for a single pending subscription change
export type PendingSubscriptionChange =
  UpdateBatchOfSubscriptionsApiArg['body']['subscriptions'][0];

export interface IntegrationsState {
  configuration: IntegrationConfiguration;
  subscriptions: {
    queryArgs: GetSubscribableResourcesApiArg | null;
    pendingChanges: Record<string, PendingSubscriptionChange>;
    loading: boolean;
    error: string | null;
  };
  integrationJobs: Record<string, PusherEventPayloadMap['integration_job_event']>;
}

const initialState: IntegrationsState = {
  configuration: {
    toolId: null,
    scope: 'personal',
    modal: null,
    // Leave profile unset by default so `useIntegrationModalManager` can
    // resolve the active profile from the connection (or provider default)
    // rather than forcing `'default'` and masking per-connection settings.
    importProfileKey: null,
  },
  subscriptions: {
    queryArgs: null,
    pendingChanges: {},
    loading: false,
    error: null,
  },
  integrationJobs: {},
};

/** Draft a subscription change - compares against server state to determine if change needed */
export const draftSubscriptionChange = createAsyncThunk<
  void,
  UpdateBatchOfSubscriptionsApiArg['body']['subscriptions'][0],
  { state: IState }
>('integrations/draftSubscriptionChange', async (payload, { getState, dispatch }) => {
  const state = getState();
  const { subscription_id, enabled: desiredEnabled } = payload;

  // Get original state from RTKQuery cache
  const queryArgs = state.ticIntegrations.subscriptions.queryArgs;
  if (!queryArgs) {
    // No cached data available, just track the change
    dispatch(integrationsSlice.actions.setDraftChange(payload));
    return;
  }

  const cachedData = ticApi.endpoints.getSubscribableResources.select(queryArgs)(state);
  const originalResource = cachedData?.data?.subscriptions?.find(
    r =>
      r.subscription_id === subscription_id ||
      r.sub_subscriptions?.find(sub => sub.subscription_id === subscription_id)
  );

  let originalEnabled: boolean;
  if (originalResource?.subscription_id === subscription_id) {
    // Root resource
    originalEnabled = originalResource.enabled;
  } else {
    // Sub-subscription
    const subResource = originalResource?.sub_subscriptions?.find(
      sub => sub.subscription_id === subscription_id
    );
    originalEnabled = subResource?.enabled ?? false;
  }

  if (desiredEnabled === originalEnabled) {
    // Desired state matches original - remove any pending change
    dispatch(integrationsSlice.actions.removeDraftChange(subscription_id));
  } else {
    // Desired state differs - track the change
    dispatch(integrationsSlice.actions.setDraftChange(payload));

    // FRONTEND CASCADING LOGIC: When disabling a parent, disable all children
    if (originalResource?.subscription_id === subscription_id && !desiredEnabled) {
      // This is a root resource being disabled - cascade to all children
      originalResource.sub_subscriptions?.forEach(subResource => {
        if (subResource.enabled) {
          // Only cascade if child is currently enabled
          dispatch(
            integrationsSlice.actions.setDraftChange({
              subscription_id: subResource.subscription_id,
              resource_id: subResource.resource_id,
              enabled: false,
            })
          );
        }
      });
    }
  }
});

const integrationsSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {
    setConfiguration: (state, { payload }: PayloadAction<Partial<IntegrationConfiguration>>) => {
      const prevProvider = state.configuration.toolId;
      const prevScope = state.configuration.scope;

      state.configuration = { ...state.configuration, ...payload };

      // Clear subscriptions if provider or scope changes
      if (
        (payload.toolId !== undefined && payload.toolId !== prevProvider) ||
        (payload.scope !== undefined && payload.scope !== prevScope)
      ) {
        state.subscriptions = initialState.subscriptions;
      }

      // If modal becomes null, clear everything (including defaultStep)
      if (payload.modal === null) {
        state.configuration = initialState.configuration;
        state.subscriptions = initialState.subscriptions;
      }
    },

    clearConfiguration: state => {
      state.configuration = initialState.configuration;
      state.subscriptions = initialState.subscriptions;
    },

    setSubscriptionQueryArgs: (
      state,
      { payload }: PayloadAction<GetSubscribableResourcesApiArg>
    ) => {
      state.subscriptions.queryArgs = payload;
      state.subscriptions.error = null;
    },

    /** Internal action to set a draft change */
    setDraftChange: (
      state,
      { payload }: PayloadAction<UpdateBatchOfSubscriptionsApiArg['body']['subscriptions'][0]>
    ) => {
      const { subscription_id } = payload;
      state.subscriptions.pendingChanges[subscription_id] = payload;
    },

    /** Internal action to remove a draft change */
    removeDraftChange: (state, { payload }: PayloadAction<string>) => {
      delete state.subscriptions.pendingChanges[payload];
    },

    clearPendingChanges: state => {
      state.subscriptions.pendingChanges = {};
    },

    restorePendingChanges: (
      state,
      { payload }: PayloadAction<UpdateBatchOfSubscriptionsApiArg['body']['subscriptions']>
    ) => {
      const changesMap: Record<
        string,
        UpdateBatchOfSubscriptionsApiArg['body']['subscriptions'][0]
      > = {};
      payload.forEach(change => {
        changesMap[change.subscription_id] = change;
      });
      state.subscriptions.pendingChanges = changesMap;
    },

    /** Set integration job status - handles both initial tracking and updates */
    setIntegrationJobStatus: (
      state,
      { payload }: PayloadAction<PusherEventPayloadMap['integration_job_event']>
    ) => {
      state.integrationJobs[payload.job_id] = payload;
    },

    /** Remove completed/failed integration jobs (cleanup) */
    removeIntegrationJobStatus: (state, { payload }: PayloadAction<string>) => {
      delete state.integrationJobs[payload];
    },

    /** Clear all integration jobs (reset) */
    clearIntegrationJobStatuses: state => {
      state.integrationJobs = {};
    },
  },
});

export const {
  setConfiguration,
  clearConfiguration,
  setSubscriptionQueryArgs,
  clearPendingChanges,
  restorePendingChanges,
  setIntegrationJobStatus,
  removeIntegrationJobStatus,
  clearIntegrationJobStatuses,
} = integrationsSlice.actions;

export default integrationsSlice.reducer;
